import { useAppServices } from "hook/services";
import React, { useEffect, useMemo, useState } from "react";
import { FaQuestionCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const OnboardingModal = ({ onClose, Update, agency }) => {
  const Service = useAppServices();
  const [onboardingSteps, setOnboardSteps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [animatedProgress, setAnimatedProgress] = useState(0);

  const navigate = useNavigate();

  const handleRoutes = (page) => {
    const newIframeUrl = `/hamza@levelupmarketplace.com/${agency?.ghl?.location_id}/${page}`;
    navigate(newIframeUrl);
    onClose();
  };

  const fetchSteps = async () => {
    try {
      setLoading(true);
      const { response } = await Service.superadmin.GET();

      if (response?.success) {
        const formattedSteps = response.data.onboarding.map((item, idx) => {
          let status = "active";
          if (item?.key && agency?.onboarding?.includes(item.key)) {
            status = "done";
          }

          if (idx === 0 && status !== "done") status = "active";

          return {
            id: item.label,
            label: item?.label || "",
            value: item?.value || "",
            link: item?.link || "",
            key: item?.key || "",
            kb_link: item?.kb_link || "",
            status,
          };
        });

        setOnboardSteps(formattedSteps);
      }
    } catch (error) {
      console.error("Failed to fetch onboarding steps", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSteps();
  }, []);

  const handleDone = async (stepKey) => {
    try {
      const alreadyExists = agency?.onboarding?.includes(stepKey);

      let updatedOnboarding = [];

      if (alreadyExists) {
        // ✅ UNDO (remove from array)
        updatedOnboarding = agency?.onboarding.filter((key) => key !== stepKey);
      } else {
        // ✅ MARK DONE (add to array)
        updatedOnboarding = [...(agency?.onboarding || []), stepKey];
      }

      const payload = {
        _id: agency?._id,
        onboarding: updatedOnboarding,
      };

      const { response } = await Service.agency.update({ payload });

      if (response?.success) {
        setOnboardSteps((prev) =>
          prev.map((step) =>
            step.key === stepKey ? { ...step, status: alreadyExists ? "active" : "done" } : step
          )
        );

        Update?.(response.data);
      }
    } catch (error) {
      console.error("Failed to update custom step", error);
    }
  };

  const progress = useMemo(() => {
    if (!onboardingSteps.length) return 0;

    const done = onboardingSteps.filter((s) => s.status === "done").length;

    return Math.round((done / onboardingSteps.length) * 100);
  }, [onboardingSteps]);

  useEffect(() => {
    let start = animatedProgress;
    let end = progress;

    if (start === end) return;

    const duration = 400;
    const stepTime = 10;
    const steps = duration / stepTime;
    const increment = (end - start) / steps;

    const interval = setInterval(() => {
      start += increment;

      if ((increment > 0 && start >= end) || (increment < 0 && start <= end)) {
        start = end;
        clearInterval(interval);
      }

      setAnimatedProgress(Math.round(start));
    }, stepTime);

    return () => clearInterval(interval);
  }, [progress]);

  return (
    <>
      <div className="modal-overlay">
        <div className="modal-content">
          <button className="close-icon" onClick={onClose}>
            ✕
          </button>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "20px",
            }}
          >
            <div>
              <h2>Welcome to Smart Directory 👋</h2>
              <p>Start your onboarding by completing these steps.</p>
            </div>
            <div
              className="progress-circle"
              aria-label={`Setup ${animatedProgress}% complete`}
              title={`Setup ${animatedProgress}% complete`}
            >
              <svg width="80" height="80">
                <circle cx="40" cy="40" r="34" stroke="#00a6a9" strokeWidth="1" fill="none" />
                <circle
                  cx="40"
                  cy="40"
                  r="34"
                  stroke="#00a6a9"
                  strokeWidth="6"
                  fill="none"
                  strokeDasharray={214}
                  strokeDashoffset={214 - (214 * animatedProgress) / 100}
                  strokeLinecap="round"
                  transform="rotate(-90 40 40)"
                />
              </svg>
              <span>{animatedProgress}%</span>
            </div>
          </div>

          {loading ? (
            <div>Loading steps...</div>
          ) : (
            <div className="steps-container">
              {onboardingSteps.map((step, idx) => (
                <div key={step.id} className="step-item">
                  <div className={`step-number ${step.status}`}>{idx + 1}</div>
                  <div className="step-info">
                    <div className="step-title">{step.label}</div>
                    <div className="step-desc">{step.value}</div>
                  </div>
                  <div className="step-action">
                    {step?.status === "active" && step?.kb_link && (
                      <button
                        className={`step-active`}
                        onClick={() => window.open(step?.kb_link, "_blank")}
                        aria-label="View help article"
                        title="View help article"
                      >
                        ?
                      </button>
                    )}
                    {step?.status === "active" && (
                      <button
                        className={`step-active`}
                        disabled={!step?.link}
                        onClick={() => handleRoutes(step?.link)}
                        aria-label="Go to this section"
                        title="Go to this section"
                      >
                        →
                      </button>
                    )}
                    {step?.status === "active" && (
                      <button
                        className={`step-active`}
                        onClick={() => handleDone(step?.key)}
                        aria-label="Mark complete"
                        title="Mark complete"
                      >
                        ✓
                      </button>
                    )}

                    {step.status === "done" && (
                      <button
                        className="status-done"
                        onClick={() => handleDone(step.key)}
                        aria-label="Mark incomplete"
                        title="Mark incomplete"
                      >
                        ✓
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <style>{`

/* ================= MODAL ================= */

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  position: relative;
  background: #ffffff;
  border-radius: 14px;
  padding: 32px 28px;
  width: 760px;
  max-width: 92%;
  box-shadow:
    0px 20px 40px rgba(0,0,0,0.08),
    0px 4px 12px rgba(0,0,0,0.04);
  animation: fadeIn .25s ease;
}

  .progress-circle {
  position: relative;
  width: 90px;
  height: 90px;
  display:flex;
  align-items:center;
  justify-content:center;
  margin-right: 20px;
  font-weight:600;
}

.progress-circle span {
  position:absolute;
  font-size:16px;
  background:#00a6a9;
  border-radius:50%;
  color:#fff;
  width:55px;
  height:55px;
  display:flex;
  align-items:center;
  justify-content:center;
}

@keyframes fadeIn {
  from { transform: translateY(10px); opacity:0 }
  to { transform: translateY(0); opacity:1 }
}

/* ================= HEADER ================= */

h2 {
  margin-bottom: 6px;
  font-weight: 600;
  font-size: 20px;
  color: #0f172a;
}

p {
  color: #64748b;
  margin-bottom: 28px;
  font-size: 14px;
}

/* ================= CLOSE ================= */

.close-icon {
  position: absolute;
  top: 14px;
  right: 16px;
  background: transparent;
  border: none;
  font-size: 18px;
  font-weight: 600;
  color: #94a3b8;
  cursor: pointer;
  transition: all .2s ease;
}

.close-icon:hover {
  color: #0f172a;
  transform: rotate(90deg);
}

/* ================= STEPS ================= */

.steps-container {
  display: flex;
  flex-direction: column;
  gap: 14px;
  height: 500px;
  overflow-y: auto;
  padding-right: 4px;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #ffffff;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  transition: all .2s ease;
}

.step-item:hover {
  border-color: #cbd5f5;
  box-shadow: 0px 4px 12px rgba(0,0,0,0.04);
}

/* ================= STEP NUMBER ================= */

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 15px;
  border: 2px solid #00a6a9;
  color: #00a6a9;
  display:flex;
  align-items:center;
  justify-content:center;
  font-weight:700;
  font-size:18px;
}

/* DONE STATE */
.step-number.done {
  background:#00a6a9;
  color:#fff;
  border-color:#00a6a9;
}

/* ================= STEP INFO ================= */

.step-info {
  flex: 1;
}

.step-title {
  font-weight: 600;
  font-size: 15px;
  color: #0f172a;
}

.step-desc {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

/* ================= ACTION ================= */

.step-action {
  display: flex;
  align-items: center;
  gap:5px;
}

.step-active{
  color: #00a6a9;
  background: transparent;
  border: 2px solid #00a6a9;
  cursor: pointer;
  padding: 4px 15px;
  border-radius: 15px;
  font-weight: 800;
  font-size: 22px;
}

.step-active:hover {
  transform: translateY(-1px);
  box-shadow: 0px 6px 14px rgba(59,130,246,0.25);
}

.step-active:disabled {
  background: #e2e8f0;
  color: #94a3b8;
  cursor: not-allowed;
  box-shadow: none;
}
.status-done {
  color: white;
  background: #00a6a9;
  padding: 4px 12px;
  border-radius: 15px;
  font-weight: 800;
  font-size: 22px;
  border: none;
  cursor: pointer;
          }

          .progress-circle circle:nth-child(2) {
  transition: stroke-dashoffset 0.6s ease-in-out;
}


`}</style>
        </div>
      </div>
    </>
  );
};

export default OnboardingModal;
